<template>
  <transition name="fade">
    <div v-if="loading" class="preloader">
      <div class="scanlines"></div>
      <div class="vignette"></div>

      <div class="terminal">
        <!-- Boot header -->
        <div class="boot-head">
          <span class="prompt">&gt;</span>
          <span class="boot-text">{{ bootText }}</span><span class="caret">_</span>
        </div>

        <div class="title-row">
          <h1 class="title">System Status</h1>
          <span class="build">v2.4.1 · build {{ build }}</span>
        </div>

        <ul class="status-list">
          <li
            v-for="(item, i) in systems"
            :key="item"
            class="status-row"
            :class="{ shown: i < revealed, done: i < checked }"
          >
            <span class="idx">{{ String(i + 1).padStart(2, '0') }}.</span>
            <span class="label">{{ item }}</span>
            <span class="leader"></span>
            <span class="ms">{{ i < checked ? stamps[i] : '' }}</span>
            <span class="state">{{ i < checked ? 'OK' : (i < revealed ? 'CHK' : '') }}</span>
            <span class="dot" :class="{ ok: i < checked }"></span>
          </li>
        </ul>

        <!-- Progress bar -->
        <div class="progress">
          <div class="track">
            <div class="fill" :style="{ width: progress + '%' }"></div>
          </div>
          <div class="pct">{{ progress }}%</div>
        </div>

        <div class="core-row">
          <div class="binary">
            <div class="bar"></div>
            <div class="rows">
              <div v-for="(row, r) in binaryRows" :key="r" class="brow">
                <span v-for="(grp, g) in row" :key="g" class="grp">{{ grp }}</span>
              </div>
            </div>
          </div>

          <!-- CPU chip: powers on as the finale, after all checks pass -->
          <div class="cpu" :class="{ on: cpuOn }">
            <svg viewBox="0 0 120 120" width="150" height="150">
              <!-- Pins -->
              <g class="pins" stroke-width="3" stroke-linecap="round">
                <line v-for="n in 6" :key="'t'+n" :x1="28 + n*11" y1="18" :x2="28 + n*11" y2="30"/>
                <line v-for="n in 6" :key="'b'+n" :x1="28 + n*11" y1="90" :x2="28 + n*11" y2="102"/>
                <line v-for="n in 6" :key="'l'+n" x1="18" :y1="28 + n*11" x2="30" :y2="28 + n*11"/>
                <line v-for="n in 6" :key="'r'+n" x1="90" :y1="28 + n*11" x2="102" :y2="28 + n*11"/>
              </g>
              <!-- Chip body -->
              <rect class="chip" x="30" y="30" width="60" height="60" rx="8"/>
              <!-- Circuit traces -->
              <g class="traces" fill="none" stroke-width="1.5">
                <path d="M45 45 H60 V55 H72"/>
                <path d="M45 75 H55 V65 H72"/>
                <path d="M75 45 V60"/>
              </g>
              <!-- Core -->
              <rect class="cpu-core" x="48" y="48" width="24" height="24" rx="4"/>
              <text class="cpu-label" x="60" y="64" text-anchor="middle">CPU</text>
            </svg>
            <div class="cpu-status">{{ cpuOn ? 'POWERED ON' : 'STANDBY' }}</div>
          </div>
        </div>

        <div class="footer" :class="{ ready: cpuOn }">
          {{ cpuOn
            ? '// ALL SYSTEMS NOMINAL — INITIALIZING INTERFACE'
            : (allDone ? '// POWERING ON CPU…' : '// RUNNING DIAGNOSTICS…') }}
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AppPreloader',
  data() {
    return {
      loading: true,
      revealed: 0,
      checked: 0,
      bootText: '',
      build: Math.random().toString(16).slice(2, 8).toUpperCase(),
      stamps: [],
      cpuOn: false,
      binaryRows: [],
      systems: [
        'PROCESSOR (CPU)', 'MOTHERBOARD', 'RAM MEMORY', 'GRAPHICS CARD (GPU)',
        'STORAGE (SSD / HDD)', 'POWER SUPPLY (PSU)', 'COOLING FAN',
        'DISPLAY / MONITOR', 'KEYBOARD & MOUSE', 'NETWORK ADAPTER'
      ]
    }
  },
  computed: {
    progress() {
      return Math.round((this.checked / this.systems.length) * 100)
    },
    allDone() {
      return this.checked >= this.systems.length
    }
  },
  created() {
    this.regenBinary()
    // Pre-generate realistic per-check latencies
    this.stamps = this.systems.map(() => (2 + Math.random() * 46).toFixed(1) + 'ms')
  },
  mounted() {
    this.typeBoot('NEXXT GEN COMPUTERS // DIAGNOSTIC BOOT SEQUENCE')

    // Standard cadence for the whole boot sequence
    const STEP = 300

    // Reveal each system line in sequence
    this._reveal = setInterval(() => {
      if (this.revealed < this.systems.length) this.revealed++
      else clearInterval(this._reveal)
    }, STEP)

    // Turn each dot green one-by-one, lagging one beat behind the reveal.
    // When the last check passes, power on the CPU as the finale.
    this._check = setInterval(() => {
      if (this.checked < this.revealed) {
        this.checked++
        if (this.checked === this.systems.length) {
          clearInterval(this._check)
          // brief beat, then CPU powers on → short pause → fade to landing page
          this._power = setTimeout(() => {
            this.cpuOn = true
            this._done = setTimeout(this.hide, STEP * 3)
          }, STEP * 2)
        }
      }
    }, STEP)

    // Continuously scramble the binary block
    this._scramble = setInterval(this.regenBinary, 200)

    // Safety net: force-hide if the boot finale somehow never fires
    this._maxWait = setTimeout(this.hide, 9000)
  },
  beforeUnmount() {
    clearInterval(this._reveal)
    clearInterval(this._check)
    clearInterval(this._scramble)
    clearTimeout(this._type)
    clearTimeout(this._power)
    clearTimeout(this._done)
    clearTimeout(this._maxWait)
  },
  methods: {
    typeBoot(str, i = 0) {
      this.bootText = str.slice(0, i)
      if (i <= str.length) {
        this._type = setTimeout(() => this.typeBoot(str, i + 1), 28)
      }
    },
    regenBinary() {
      const grp = () => Array.from({ length: 8 }, () => Math.round(Math.random())).join('')
      this.binaryRows = Array.from({ length: 9 }, () => [grp(), grp(), grp()])
    },
    hide() {
      this.loading = false
      window.dispatchEvent(new Event('preloader:done'))
    }
  }
}
</script>

<style scoped>
.preloader {
  position: fixed;
  inset: 0;
  background: radial-gradient(120% 120% at 30% 20%, #202020 0%, #131313 60%, #0d0d0d 100%);
  z-index: 9999;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* CRT scanlines */
.scanlines {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.02) 0px,
    rgba(255, 255, 255, 0.02) 1px,
    transparent 2px,
    transparent 3px
  );
  animation: scan 8s linear infinite;
}
@keyframes scan { to { background-position: 0 100px; } }

.vignette {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(120% 120% at 50% 50%, transparent 55%, rgba(0, 0, 0, 0.55) 100%);
}

.terminal {
  position: relative;
  width: min(600px, 88vw);
  color: #d8d8d8;
  font-family: 'AuxMono', 'SF Mono', 'Menlo', 'Consolas', monospace;
  font-size: 12px;
}

/* Boot header */
.boot-head {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #F58523;
  font-size: 11px;
  letter-spacing: 1px;
  margin-bottom: 22px;
  text-transform: uppercase;
}
.prompt { opacity: 0.8; }
.boot-text { color: #9aa0a6; }
.caret {
  color: #F58523;
  animation: blink 1s steps(1) infinite;
}
@keyframes blink { 50% { opacity: 0; } }

.title-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 0 0 1.6rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding-bottom: 12px;
}
.title {
  font-family: 'Space Grotesk', 'Helvetica Neue', Arial, sans-serif;
  font-weight: 600;
  font-size: 26px;
  letter-spacing: 0.5px;
  margin: 0;
  color: #eaeaea;
}
.title::before {
  content: '◤ ';
  color: #F58523;
  font-size: 18px;
  vertical-align: 2px;
}
.build {
  font-size: 10px;
  letter-spacing: 1px;
  color: #5c5c5c;
  text-transform: uppercase;
}

.status-list {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
}
.status-row {
  display: flex;
  align-items: center;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  line-height: 1.9;
  padding: 2px 0;
  opacity: 0;
  transform: translateX(-8px);
  filter: blur(2px);
  transition:
    opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}
.status-row.shown {
  opacity: 1;
  transform: translateX(0);
  filter: blur(0);
}
.idx { color: #7a7a7a; width: 42px; margin-right: 4px; }
.label { color: #d8d8d8; }
.leader {
  flex: 1;
  margin: 0 12px;
  height: 1px;
  align-self: center;
  background-image: linear-gradient(to right, #3a3a3a 40%, transparent 40%);
  background-size: 6px 1px;
  opacity: 0.6;
}
.ms {
  width: 56px;
  text-align: right;
  margin-right: 14px;
  color: #5c5c5c;
  font-size: 11px;
  letter-spacing: 0.5px;
}
.state {
  width: 30px;
  text-align: right;
  margin-right: 14px;
  color: #6a6a6a;
  transition: color 0.3s ease;
}
.status-row.done .state { color: #F58523; }
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #3a3a3a;
  transition: background 0.3s ease, box-shadow 0.3s ease;
}
.dot.ok {
  background: #F58523;
  box-shadow: 0 0 10px rgba(245, 133, 35, 0.9);
}

/* Progress */
.progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 2rem;
}
.track {
  flex: 1;
  height: 2px;
  background: #2c2c2c;
  border-radius: 2px;
  overflow: hidden;
}
.fill {
  height: 100%;
  background: linear-gradient(to right, #b35e14, #F58523);
  box-shadow: 0 0 8px rgba(245, 133, 35, 0.6);
  transition: width 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}
.pct { color: #9aa0a6; width: 36px; text-align: right; }

.core-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 1.6rem;
}
.binary {
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding-left: 6px;
}

/* CPU chip */
.cpu {
  text-align: center;
  transition: transform 0.5s ease;
}
.cpu svg { display: block; }
.cpu .pins { stroke: #3a3a3a; transition: stroke 0.6s ease; }
.cpu .chip {
  fill: #1a1a1a;
  stroke: #3a3a3a;
  stroke-width: 2;
  transition: stroke 0.6s ease, fill 0.6s ease;
}
.cpu .traces { stroke: #333; transition: stroke 0.6s ease; }
.cpu .cpu-core {
  fill: #222;
  transition: fill 0.6s ease, filter 0.6s ease;
}
.cpu .cpu-label {
  fill: #555;
  font-family: 'AuxMono', monospace;
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 1px;
  transition: fill 0.6s ease;
}
.cpu-status {
  margin-top: 6px;
  font-size: 10px;
  letter-spacing: 2px;
  color: #5c5c5c;
  transition: color 0.6s ease;
}

/* Powered-on state */
.cpu.on { transform: scale(1.04); }
.cpu.on .pins { stroke: #F58523; }
.cpu.on .chip {
  fill: #0e1f16;
  stroke: #F58523;
  filter: drop-shadow(0 0 6px rgba(245, 133, 35, 0.6));
}
.cpu.on .traces {
  stroke: #F58523;
  stroke-dasharray: 4 4;
  animation: flow 1s linear infinite;
}
@keyframes flow { to { stroke-dashoffset: -8; } }
.cpu.on .cpu-core {
  fill: #F58523;
  filter: drop-shadow(0 0 8px rgba(245, 133, 35, 0.9));
  animation: corepulse 1.3s ease-in-out infinite;
}
@keyframes corepulse {
  50% { fill: #ffb066; filter: drop-shadow(0 0 14px rgba(245, 133, 35, 1)); }
}
.cpu.on .cpu-label { fill: #0e1f16; }
.cpu.on .cpu-status {
  color: #F58523;
  animation: pulse 1.4s ease-in-out infinite;
}
.bar {
  position: relative;
  width: 3px;
  background: #2a2a2a;
  border-radius: 2px;
  overflow: hidden;
}
/* Live signal pulse traveling down the pipe */
.bar::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 40%;
  border-radius: 2px;
  background: linear-gradient(
    to bottom,
    transparent,
    #F58523 45%,
    #ffb066 50%,
    #F58523 55%,
    transparent
  );
  box-shadow: 0 0 10px rgba(245, 133, 35, 0.8);
  animation: signal 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}
@keyframes signal {
  0% { top: -40%; }
  100% { top: 100%; }
}
.rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  letter-spacing: 1px;
  color: #6f6f6f;
}
.brow { display: flex; gap: 24px; line-height: 1.5; }
.brow:nth-child(3n) { margin-bottom: 14px; }
.brow:last-child { margin-bottom: 0; }
.grp { width: 72px; }

.footer {
  font-size: 11px;
  letter-spacing: 1.5px;
  color: #6a6a6a;
  text-transform: uppercase;
}
.footer.ready {
  color: #F58523;
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse { 50% { opacity: 0.5; } }

.fade-leave-active { transition: opacity 0.6s ease; }
.fade-leave-to { opacity: 0; }

/* mobile: keep the binary block + CPU chip inside the viewport (no crop) */
@media (max-width: 600px) {
  .core-row { gap: 12px; }
  .brow { gap: 12px; font-size: 10px; }
  .binary { gap: 8px; padding-left: 0; }
  .cpu svg { width: 108px; height: 108px; }
  .cpu-status { font-size: 8px; }
}
</style>
